import { Button, Modal, RadioGroup } from "@bp/ui-components";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";
import { getFragmentData, graphql } from "../../../client/gql";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import css from "../orgList.module.scss";

// Colocated fragment: the application fields this bulk-assign picker needs.
const BulkLicenseModal_Application = graphql(`
  fragment BulkLicenseModal_Application on Application {
    uuid
    name
    licensedOrganizations {
      uuid
    }
  }
`);

const BulkLicenseModalQuery = graphql(`
  query BulkLicenseModal_Query {
    applications(options: { sort: { name: ASC } }) {
      ...BulkLicenseModal_Application
    }
  }
`);

const BulkLicenseModalUpdateApplication = graphql(`
  mutation BulkLicenseModal_UpdateApplication(
    $where: ApplicationWhere!
    $update: ApplicationUpdateInput!
  ) {
    updateApplications(where: $where, update: $update) {
      applications {
        uuid
      }
    }
  }
`);

type Props = {
  org: { uuid: string; name: string };
  profileUuids: readonly string[];
  onClose: () => void;
  onSaved: () => void;
};

/** Grant one of the org's licensed products to all selected members at once. */
export function BulkLicenseModal({ org, profileUuids, onClose, onSaved }: Props) {
  const [{ data }] = useQuery({ query: BulkLicenseModalQuery });
  const [, updateApplication] = useMutation(BulkLicenseModalUpdateApplication);
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const apps = useMemo(
    () =>
      getFragmentData(BulkLicenseModal_Application, data?.applications ?? []).filter((a) =>
        (a.licensedOrganizations ?? []).some((o) => o.uuid === org.uuid),
      ),
    [data, org.uuid],
  );

  const assign = async () => {
    if (!chosen) {
      return;
    }
    setBusy(true);
    const res = await updateApplication({
      where: { uuid: chosen },
      update: {
        accessGrantedProfiles: [
          { connect: profileUuids.map((uuid) => ({ where: { node: { uuid } } })) },
        ],
      },
    });
    setBusy(false);
    if (res.error) {
      showUserErrorToast({ text: "Zuweisen fehlgeschlagen", error: res.error });
      return;
    }
    const app = apps.find((a) => a.uuid === chosen);
    showSuccessToast(`${app?.name ?? "Produkt"} an ${profileUuids.length} Mitglied(er) zugewiesen`);
    onSaved();
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      width="s"
      title={`Lizenz zuweisen — ${profileUuids.length} Mitglied(er)`}
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>
            Die ausgewählten Mitglieder erhalten Zugriff auf das Produkt.
          </span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button hierarchy="primary" disabled={busy || !chosen} onClick={() => void assign()}>
              Zuweisen
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        <span style={{ fontSize: 12, color: "var(--color-grey)" }}>
          Produkt wählen — die ausgewählten Mitglieder erhalten Zugriff darauf.
        </span>
        {apps.length === 0 && (
          <span style={{ fontSize: 13, color: "var(--color-grey)" }}>
            {org.name} hat keine lizenzierten Produkte.
          </span>
        )}
        {apps.length > 0 && (
          <RadioGroup
            name="bulk-license-app"
            options={apps.map((a) => ({ value: a.uuid, label: a.name }))}
            value={chosen ?? ""}
            onChange={(v) => setChosen(v)}
          />
        )}
      </div>
    </Modal>
  );
}
